import { useState, useEffect, useMemo, useRef } from 'react'
import { useParams, Link } from 'react-router-dom'
import {
  ArrowLeft,
  ArrowRight,
  Sparkles,
  BookOpen,
  Search,
  X,
  ChevronDown,
  ChevronUp,
} from 'lucide-react'
import {
  getRelease,
  listReleases,
  recordChangelogVisit,
  type ReleaseNote,
  type ReleaseChange,
  type ReleaseSummary,
} from '../lib/api'
import { RichMarkdown } from '../components/content/RichMarkdown'
import { ContentSkeleton } from '../components/ui/Skeleton'
import { ImageLightbox } from '../components/ui/ImageLightbox'
import { consumeChangelogUtmSource, readChangelogSource } from '../lib/changelogSource'
import { cn } from '../lib/utils'

const categoryConfig: Record<string, { label: string; color: string; bgColor: string }> = {
  major: { label: 'Major Release', color: 'text-purple-400', bgColor: 'bg-purple-500/15' },
  feature: { label: 'New Feature', color: 'text-blue-400', bgColor: 'bg-blue-500/15' },
  improvement: { label: 'Improvement', color: 'text-green-400', bgColor: 'bg-green-500/15' },
  bugfix: { label: 'Bug Fix', color: 'text-amber-400', bgColor: 'bg-amber-500/15' },
}

const CATEGORY_ORDER = ['feature', 'improvement', 'bugfix', 'major']
const COLLAPSE_THRESHOLD = 5

function CategoryBadge({ category }: { category: string }) {
  const config = categoryConfig[category] || {
    label: category,
    color: 'text-zinc-400',
    bgColor: 'bg-zinc-500/15',
  }
  return (
    <span className={cn('px-2.5 py-0.5 text-xs font-medium rounded-full', config.bgColor, config.color)}>
      {config.label}
    </span>
  )
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}


function HighlightCard({ change }: { change: ReleaseChange }) {
  return (
    <div className="border border-border rounded-lg p-4">
      <div className="flex items-center gap-2 mb-1.5">
        <CategoryBadge category={change.category} />
        <h3 className="font-medium">{change.title}</h3>
      </div>
      {change.description && (
        <p className="text-sm text-muted-foreground">{change.description}</p>
      )}
      {change.image && (
        <ImageLightbox src={change.image} alt={change.title} />
      )}
    </div>
  )
}

function CompactItem({ change }: { change: ReleaseChange }) {
  return (
    <li className="flex items-start gap-2 py-1">
      <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-muted-foreground/40 flex-shrink-0" />
      <span className="text-sm text-muted-foreground">{change.title}</span>
    </li>
  )
}

export default function ChangelogDetail() {
  const { slug } = useParams<{ slug: string }>()
  const [release, setRelease] = useState<ReleaseNote | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [changeSearch, setChangeSearch] = useState('')
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({})
  const [allReleases, setAllReleases] = useState<ReleaseSummary[]>([])
  // StrictMode double-mounts useEffect in dev; latch by slug so prev/next
  // navigation still records a new visit but a single mount records once.
  const recordedSlug = useRef<string | null>(null)

  useEffect(() => {
    if (!slug) return
    setLoading(true)
    setChangeSearch('')
    setExpandedCategories({})

    const shouldRecord = recordedSlug.current !== slug
    const signals = shouldRecord ? readChangelogSource() : null
    if (shouldRecord) {
      recordedSlug.current = slug
      consumeChangelogUtmSource()
    }

    getRelease(slug)
      .then((data) => {
        setRelease(data)
        if (signals) {
          recordChangelogVisit(data.version, signals).catch(() => {})
        }
      })
      .catch((err) => setError(err.message || 'Failed to load release'))
      .finally(() => setLoading(false))
  }, [slug])

  // Load the version index once so we can render prev/next links.
  useEffect(() => {
    listReleases()
      .then((data) => setAllReleases(data))
      .catch(() => setAllReleases([]))
  }, [])

  // Releases come back newest-first from listReleases (sorted desc by version).
  // Prev = older release (one position later in the array).
  // Next = newer release (one position earlier).
  const { prevRelease, nextRelease } = useMemo(() => {
    if (!release || allReleases.length === 0) {
      return { prevRelease: null as ReleaseSummary | null, nextRelease: null as ReleaseSummary | null }
    }
    const idx = allReleases.findIndex((r) => r.version === release.version)
    if (idx === -1) return { prevRelease: null, nextRelease: null }
    return {
      prevRelease: idx + 1 < allReleases.length ? allReleases[idx + 1] : null,
      nextRelease: idx - 1 >= 0 ? allReleases[idx - 1] : null,
    }
  }, [release, allReleases])

  // Group ALL changes by category
  const groupedChanges = useMemo(() => {
    if (!release) return {}
    const groups: Record<string, ReleaseChange[]> = {}
    for (const c of release.changes) {
      if (!groups[c.category]) groups[c.category] = []
      groups[c.category].push(c)
    }
    return groups
  }, [release])

  const orderedCategories = useMemo(() => {
    return CATEGORY_ORDER.filter((cat) => groupedChanges[cat]?.length)
  }, [groupedChanges])

  // Search across all changes
  const filteredChanges = useMemo(() => {
    if (!release || !changeSearch) return null
    const q = changeSearch.toLowerCase()
    return release.changes.filter(
      (c) =>
        c.title.toLowerCase().includes(q) ||
        (c.description && c.description.toLowerCase().includes(q))
    )
  }, [release, changeSearch])

  function toggleCategory(cat: string) {
    setExpandedCategories((prev) => ({ ...prev, [cat]: !prev[cat] }))
  }

  if (loading) {
    return (
      <div>
        <Link
          to="/changelog"
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Changelog
        </Link>
        <ContentSkeleton />
      </div>
    )
  }

  if (error || !release) {
    const isNotFound = error?.toLowerCase().includes('not found')
    return (
      <div>
        <Link
          to="/changelog"
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Changelog
        </Link>
        <p className="text-red-400">
          {isNotFound ? 'Release not found.' : error || 'Release not found.'}
        </p>
      </div>
    )
  }

  const sectionsMarkdown = release.sections
    .map((s) => `## ${s.heading}\n\n${s.content}`)
    .join('\n\n')

  return (
    <div>
      {/* Back navigation */}
      <Link
        to="/changelog"
        className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-6"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Changelog
      </Link>

      {/* Release header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-3">
          <span className="text-xs font-mono text-muted-foreground">{release.version}</span>
          <span className="text-xs text-muted-foreground">{formatDate(release.date)}</span>
        </div>
        <h1 className="text-2xl font-bold mb-2">{release.title}</h1>
        <p className="text-lg text-muted-foreground">{release.summary}</p>
      </div>

      {/* Sections (What's New narrative) rendered above changes */}
      {sectionsMarkdown && (
        <div className="mb-8">
          <RichMarkdown content={sectionsMarkdown} />
        </div>
      )}

      {/* Changes section */}
      {release.changes.length > 0 && (
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-amber-400" />
              <h2 className="text-xl font-semibold">Changes</h2>
              <span className="text-xs text-muted-foreground">
                {release.changes.length} items
              </span>
            </div>

            {/* Search input */}
            <div className="relative w-48">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <input
                type="text"
                placeholder="Filter changes..."
                value={changeSearch}
                onChange={(e) => setChangeSearch(e.target.value)}
                className="w-full pl-8 pr-7 py-1.5 rounded-lg border border-border bg-card text-xs focus:outline-none focus:ring-2 focus:ring-primary/50"
              />
              {changeSearch && (
                <button
                  onClick={() => setChangeSearch('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>

          {/* Filtered flat list (when searching) */}
          {filteredChanges !== null ? (
            filteredChanges.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">
                No changes match your search.
              </p>
            ) : (
              <div className="space-y-3">
                {filteredChanges.map((change, i) =>
                  change.highlight ? (
                    <HighlightCard key={i} change={change} />
                  ) : (
                    <div key={i} className="flex items-start gap-2 py-1 px-1">
                      <CategoryBadge category={change.category} />
                      <span className="text-sm">{change.title}</span>
                    </div>
                  )
                )}
              </div>
            )
          ) : (
            /* Categorized grouped view (default) */
            <div className="space-y-8">
              {orderedCategories.map((cat) => {
                const items = groupedChanges[cat]
                const highlighted = items.filter((c) => c.highlight)
                const compact = items.filter((c) => !c.highlight)
                const isExpanded = expandedCategories[cat]
                const totalCount = items.length
                const needsCollapse = totalCount > COLLAPSE_THRESHOLD
                const visibleCompact =
                  needsCollapse && !isExpanded
                    ? compact.slice(0, Math.max(0, COLLAPSE_THRESHOLD - highlighted.length))
                    : compact
                const hiddenCount = compact.length - visibleCompact.length

                return (
                  <div key={cat} className="border border-border rounded-lg p-4 bg-card">
                    {/* Category header */}
                    <div className="flex items-center gap-2 mb-3">
                      <CategoryBadge category={cat} />
                      <span className="text-xs text-muted-foreground">
                        {totalCount} {totalCount === 1 ? 'change' : 'changes'}
                      </span>
                    </div>

                    {/* Highlighted items as feature cards */}
                    {highlighted.length > 0 && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-3">
                        {highlighted.map((change, i) => (
                          <div key={i} className="border border-border/50 rounded-lg p-3 bg-secondary/20">
                            <h3 className="font-medium mb-1">{change.title}</h3>
                            {change.description && (
                              <p className="text-sm text-muted-foreground line-clamp-3">{change.description}</p>
                            )}
                            {change.image && (
                              <ImageLightbox src={change.image} alt={change.title} />
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Compact items as bullet list */}
                    {visibleCompact.length > 0 && (
                      <ul className="space-y-0.5">
                        {visibleCompact.map((change, i) => (
                          <CompactItem key={i} change={change} />
                        ))}
                      </ul>
                    )}

                    {/* Show more/less toggle */}
                    {needsCollapse && hiddenCount > 0 && (
                      <button
                        onClick={() => toggleCategory(cat)}
                        className="mt-2 flex items-center gap-1.5 text-xs text-primary hover:underline"
                      >
                        {isExpanded ? (
                          <>
                            <ChevronUp className="w-3.5 h-3.5" />
                            Show less
                          </>
                        ) : (
                          <>
                            <ChevronDown className="w-3.5 h-3.5" />
                            Show {hiddenCount} more
                          </>
                        )}
                      </button>
                    )}
                    {needsCollapse && hiddenCount === 0 && isExpanded && (
                      <button
                        onClick={() => toggleCategory(cat)}
                        className="mt-2 flex items-center gap-1.5 text-xs text-primary hover:underline"
                      >
                        <ChevronUp className="w-3.5 h-3.5" />
                        Show less
                      </button>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}

      {/* Prev / Next release nav — saves a trip back to the index. */}
      {(prevRelease || nextRelease) && (
        <nav
          aria-label="Release navigation"
          className="mt-10 grid grid-cols-2 gap-3 border-t border-border pt-6"
        >
          {prevRelease ? (
            <Link
              to={`/changelog/${prevRelease.version}`}
              className="group flex items-center gap-3 rounded-lg border border-border bg-card p-4 hover:border-primary/30 transition-colors"
            >
              <ArrowLeft className="w-4 h-4 text-muted-foreground group-hover:text-foreground flex-shrink-0" />
              <div className="min-w-0">
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
                  Previous release
                </div>
                <div className="flex items-baseline gap-2 mt-0.5">
                  <span className="text-xs font-mono text-muted-foreground">
                    {prevRelease.version}
                  </span>
                  <span className="text-sm font-medium truncate">{prevRelease.title}</span>
                </div>
              </div>
            </Link>
          ) : (
            <div />
          )}
          {nextRelease ? (
            <Link
              to={`/changelog/${nextRelease.version}`}
              className="group flex items-center justify-end gap-3 rounded-lg border border-border bg-card p-4 hover:border-primary/30 transition-colors text-right"
            >
              <div className="min-w-0">
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
                  Next release
                </div>
                <div className="flex items-baseline gap-2 mt-0.5 justify-end">
                  <span className="text-sm font-medium truncate">{nextRelease.title}</span>
                  <span className="text-xs font-mono text-muted-foreground">
                    {nextRelease.version}
                  </span>
                </div>
              </div>
              <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-foreground flex-shrink-0" />
            </Link>
          ) : (
            <div />
          )}
        </nav>
      )}

      {/* Related guide sections (cross-reference) */}
      {release.guide_sections && release.guide_sections.length > 0 && (
        <div className="mt-8 p-4 rounded-lg bg-secondary/50 border border-border">
          <div className="flex items-start gap-3">
            <BookOpen className="w-4 h-4 text-primary mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-medium text-foreground mb-2">
                Learn more about these features:
              </p>
              <div className="flex flex-wrap gap-2">
                {release.guide_sections.map((guideSlug) => (
                  <Link
                    key={guideSlug}
                    to={`/guide/${guideSlug}`}
                    className="inline-flex items-center px-3 py-1 text-sm rounded-md bg-card border border-border text-primary hover:bg-secondary transition-colors"
                  >
                    {guideSlug
                      .split('-')
                      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
                      .join(' ')}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
